import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Checkbox } from '../ui/checkbox.jsx';
import { createCustomerOrganisation } from '../../services/api';

const ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'subcontractor', label: 'Subcontractor' }
];

/**
 * QuickCreateOrganisation - Minimal dialog for creating an organisation inline
 *
 * Pre-fills name from search term. On save, creates the org and passes it back
 * so the lookup can auto-select it.
 */
export default function QuickCreateOrganisation({ open, onOpenChange, initialName = '', onCreated }) {
  const [name, setName] = useState('');
  const [roles, setRoles] = useState(['customer']);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initialName);
      setRoles(['customer']);
      setPhone('');
      setEmail('');
      setError('');
    }
  }, [open, initialName]);

  const toggleRole = (role) => {
    setRoles((prev) =>
      prev.includes(role)
        ? prev.filter((r) => r !== role)
        : [...prev, role]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (roles.length === 0) {
      setError('Select at least one role');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const phones = phone.trim() ? JSON.stringify([{ number: phone.trim(), description: 'Main' }]) : '[]';
      const emails = email.trim() ? JSON.stringify([{ email: email.trim(), description: 'Main' }]) : '[]';

      const created = await createCustomerOrganisation({
        name: name.trim(),
        roles: roles.join(','),
        phones,
        emails
      });

      onCreated?.(created);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create organisation:', err);
      setError(err.message || 'Failed to create organisation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Organisation</DialogTitle>
          <DialogDescription>
            Quick-create with basic details. You can add more information later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div>
            <Label htmlFor="org-name" className="text-xs font-medium">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Organisation name"
              className="h-8 text-sm mt-1"
              autoFocus
            />
          </div>

          {/* Roles */}
          <div>
            <Label className="text-xs font-medium">
              Roles <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-4 mt-1.5">
              {ROLE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={roles.includes(opt.value)}
                    onCheckedChange={() => toggleRole(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="org-phone" className="text-xs font-medium">Phone</Label>
            <Input
              id="org-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 09 123 4567"
              className="h-8 text-sm mt-1"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="org-email" className="text-xs font-medium">Email</Label>
            <Input
              id="org-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. info@company.co.nz"
              className="h-8 text-sm mt-1"
            />
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
